import { Component, inject, OnInit, signal } from '@angular/core';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav';
import { MatListItem, MatNavList } from '@angular/material/list';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatDivider } from '@angular/material/divider';
import { AuthenticationFeatureService, EmployeeFeatureService, TimesheetsFeatureService } from '@features';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ProjectsFeatureService } from '@features';
import { HttpClient } from '@angular/common/http';
import { ApiConstants } from '../../../constants/api.constants';
import { catchError, filter, of, switchMap, take } from 'rxjs';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';

interface MasterTeamMembersRoot {
  value: { email: string; role: string }[];
}

@Component({
  selector: 'app-navigation-bar',
  standalone: true,
  imports: [MatToolbar, MatIcon, MatIconButton, MatSidenavContainer, MatNavList, MatSidenavModule, MatListItem, RouterLink, RouterOutlet, MatDivider, RouterLinkActive, MatProgressSpinnerModule],
  templateUrl: './navigation-bar.component.html',
  styleUrl: './navigation-bar.component.css',
})
export class NavigationBarComponent implements OnInit {
  _authFeature = inject(AuthenticationFeatureService);
  _employeeFeature = inject(EmployeeFeatureService);
  _timesheetsFeature = inject(TimesheetsFeatureService);
  _projectsFeature = inject(ProjectsFeatureService);
  private _httpClient = inject(HttpClient);
  private _broadcastService = inject(MsalBroadcastService);

  isProjectManager = signal(false);

  constructor() {}

  ngOnInit() {
    this._broadcastService.inProgress$.pipe(
      filter((status) => status === InteractionStatus.None),
      take(1),
      switchMap(() => {
        const email = this._authFeature.getInstance().getActiveAccount()?.username ?? '';
        if (!email) return of({ value: [] });
        const url = `${ApiConstants.MASTER_TEAM_MEMBERS_URL}?$filter=email eq '${email}'`;
        return this._httpClient.get<MasterTeamMembersRoot>(url).pipe(
          catchError(() => of({ value: [] })),
        );
      }),
    ).subscribe((res) => {
      this.isProjectManager.set(res.value.some((m) => m.role === 'Project_x0020_Manager'));
    });
  }

  get displayName(): string {
    return this._employeeFeature.employee()?.displayName ?? this._authFeature.getInstance().getActiveAccount()?.name ?? '';
  }

  get displayEmail(): string {
    return this._employeeFeature.employee()?.email ?? this._authFeature.getInstance().getActiveAccount()?.username ?? '';
  }

  logout() {
    this._authFeature.logout();
  }

  resetTimesheetLinesFilters() {
    this._timesheetsFeature.resetTimesheetLinesFilter();
  }

  resetProjectFilters() {
    this._projectsFeature.resetProjectsFilter();
  }
}
